import * as React from 'react'

import { Features } from 'generated/featureDefinitions'
import {
  FetchPolicyAllowancesDocument,
  FetchTimeoffDataDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import type { NavigationRoutesFn } from 'modules/core/lib/react-router'

import { getSettingPageMeta } from 'modules/settings/lib/getSettingPageMeta'
import { TabTypes } from 'modules/timeoff/lib/TimeoffSettings'

const AddAllowanceModalPage = React.lazy(
  () => import('modules/timeoff/pages/policies_settings/AddAllowanceModalPage')
)
const AddEmployeeModalPage = React.lazy(
  () => import('modules/timeoff/pages/policies_settings/AddEmployeeModalPage')
)
const AddPolicyModalPage = React.lazy(
  () => import('modules/timeoff/pages/policies_settings/AddPolicyModalPage')
)
const BlockedPeriodsDetailsPage = React.lazy(
  () => import('modules/timeoff/pages/policies_settings/BlockedPeriodsDetailsPage')
)
const BlockedPeriodsRootPage = React.lazy(
  () => import('modules/timeoff/pages/policies_settings/BlockedPeriodsRootPage')
)
const EditAllowancePage = React.lazy(
  () => import('modules/timeoff/pages/policies_settings/EditAllowancePage')
)
const EditLeaveTypePage = React.lazy(
  () => import('modules/timeoff/pages/policies_settings/EditLeaveTypePage')
)
const EmployeeListPage = React.lazy(
  () => import('modules/timeoff/pages/policies_settings/EmployeeListPage')
)
const MoveEmployeeModalPage = React.lazy(
  () => import('modules/timeoff/pages/policies_settings/MoveEmployeeModalPage')
)
const NewAllowancePage = React.lazy(
  () => import('modules/timeoff/pages/policies_settings/NewAllowancePage')
)
const NewLeaveTypePage = React.lazy(
  () => import('modules/timeoff/pages/policies_settings/NewLeaveTypePage')
)
const PoliciesRootPage = React.lazy(
  () => import('modules/timeoff/pages/policies_settings/PoliciesRootPage')
)
const RemoveModalPage = React.lazy(
  () => import('modules/timeoff/pages/policies_settings/RemoveModalPage')
)
const TimeoffPolicyDetailsPage = React.lazy(
  () => import('modules/timeoff/pages/policies_settings/TimeoffPolicyDetailsPage')
)
const TimeoffPolicyRootPage = React.lazy(
  () => import('modules/timeoff/pages/policies_settings/TimeoffPolicyRootPage')
)
const RedirectToPolicyDetails = React.lazy(
  () => import('modules/timeoff/pages/settings/RedirectToPolicyDetails')
)
const RedirectToTab = React.lazy(() => import('modules/timeoff/pages/settings/RedirectToTab'))

const RootPage = React.lazy(() => import('modules/timeoff/pages/settings/RootPage'))

const settingsRoutes: NavigationRoutesFn = (navigation) => {
  return [
    {
      path: navigation.settings.timeoffPolicies.path,
      element: <RootPage />,
      handle: {
        meta: () => getSettingPageMeta(navigation, 'time_off'),
      },
      children: [
        {
          index: true,
          element: <RedirectToTab />,
        },
        {
          path: navigation.settings.timeoffPolicies.new.path,
          element: <AddPolicyModalPage />,
        },
        {
          path: navigation.settings.timeoffPolicies.newLeaveType.path,
          element: <NewLeaveTypePage />,
          handle: {
            meta: () => ({
              title: i18n.t('settings.leave_types.add_modal.title'),
            }),
          },
        },
        {
          path: navigation.settings.timeoffPolicies.editLeaveTypeId.path,
          element: <EditLeaveTypePage />,
          handle: {
            meta: ({ watchQuery, session }) =>
              watchQuery({
                query: FetchTimeoffDataDocument,
                variables: {
                  companyIds: [session.company.id],
                  featureIds: [Features.timeoff_management, Features.timeoff_blocked_periods],
                  fetchBlockedPeriods: false,
                  fetchLeaveTypes: true,
                  fetchPolicies: false,
                },
              }).map(({ data }) => {
                const leaveTypeIdParam = window.location.href.split('/').at(-1)
                if (leaveTypeIdParam) {
                  const leaveType = data?.timeoff?.leaveTypes?.find(
                    (lt) => lt.id === leaveTypeIdParam
                  )
                  return {
                    title: leaveType?.translatedName,
                  }
                }
              }),
          },
        },
        {
          path: navigation.settings.timeoffPolicies.policies.path,
          element: <PoliciesRootPage />,
          handle: {
            meta: () => ({
              title: i18n.t('settings.timeoff_policies.list.title'),
            }),
          },
          children: [
            {
              path: navigation.settings.timeoffPolicies.policies.removeId.path,
              element: <RemoveModalPage />,
            },
          ],
        },
        {
          path: navigation.settings.timeoffPolicies.absenceTypes.path,
          element: <PoliciesRootPage />,
          handle: {
            meta: () => ({
              title: i18n.t('settings.leave_types.list.title'),
            }),
          },
        },
        {
          path: navigation.settings.timeoffPolicies.approvalGroups.path,
          element: <PoliciesRootPage />,
          handle: {
            meta: () => ({
              title: i18n.t('settings.time_off.approvals.title'),
            }),
          },
        },
        {
          path: navigation.settings.timeoffPolicies.blockedPeriods.path,
          element: <PoliciesRootPage />,
          handle: {
            meta: () => ({
              title: i18n.t('settings.time_off.blocked_periods.title'),
            }),
          },
        },
        {
          path: navigation.settings.timeoffPolicies.id.path,
          element: <TimeoffPolicyRootPage />,
          handle: {
            meta: ({ params, watchQuery }) => {
              const id = params['id']
              // Validate that id is not a tab name like "policies", "absence-types", etc.
              const validTabIds: string[] = Object.values(TabTypes)
              if (!id || validTabIds.includes(id)) {
                return { title: '' }
              }
              return watchQuery({
                query: FetchPolicyAllowancesDocument,
                variables: {
                  policyId: id,
                },
              }).map(({ data }) => {
                const title = data?.timeoff.policiesConnection.nodes[0]?.name || ''
                return {
                  title,
                  description: data?.timeoff.policiesConnection.nodes[0]?.name,
                }
              })
            },
          },
          children: [
            {
              index: true,
              element: <RedirectToPolicyDetails />,
            },
            {
              path: navigation.settings.timeoffPolicies.id.details.path,
              element: <TimeoffPolicyDetailsPage />,
              children: [
                {
                  path: navigation.settings.timeoffPolicies.id.details.addAllowance.path,
                  element: <AddAllowanceModalPage />,
                },
              ],
            },
            {
              path: navigation.settings.timeoffPolicies.id.allowances.path,
              children: [
                {
                  path: navigation.settings.timeoffPolicies.id.allowances.new.path,
                  element: <NewAllowancePage />,
                },
                {
                  path: navigation.settings.timeoffPolicies.id.allowances.allowanceId.path,
                  element: <EditAllowancePage />,
                  handle: {
                    meta: ({ params, watchQuery }) =>
                      watchQuery({
                        query: FetchPolicyAllowancesDocument,
                        variables: {
                          policyId: params['id'] ?? '',
                        },
                      }).map(({ data }) => {
                        // Dirty little trick, maybe it can be done differently; the problem
                        // is that this allowance page has not been migrated yet to graphQL
                        const allowanceIdParam = window.location.href.split('/').at(-1)
                        if (allowanceIdParam) {
                          const allowanceId = allowanceIdParam
                          const foundAllowance = data?.timeoff.allowancesConnection.nodes.find(
                            (item) => item.id === allowanceId
                          )
                          return {
                            title: foundAllowance?.name,
                          }
                        }
                      }),
                  },
                },
              ],
            },
            {
              path: navigation.settings.timeoffPolicies.id.employees.path,
              element: <EmployeeListPage />,
              children: [
                {
                  path: navigation.settings.timeoffPolicies.id.employees.add.path,
                  element: <AddEmployeeModalPage />,
                },
                {
                  path: navigation.settings.timeoffPolicies.id.employees.moveEmployeeId.path,
                  element: <MoveEmployeeModalPage />,
                },
              ],
            },
          ],
        },
        {
          path: navigation.settings.timeoffPolicies.blockedPeriodsId.path,
          element: <BlockedPeriodsRootPage />,
          handle: {
            meta: ({ watchQuery, session }) =>
              watchQuery({
                query: FetchTimeoffDataDocument,
                variables: {
                  companyIds: [session.company.id],
                  featureIds: [Features.timeoff_management, Features.timeoff_blocked_periods],
                  fetchBlockedPeriods: true,
                  fetchLeaveTypes: false,
                  fetchPolicies: false,
                },
              }).map(({ data }) => {
                const blockedPeriodIdParam = window.location.href.split('/').at(-1)
                if (blockedPeriodIdParam) {
                  const blockedPeriod = data?.timeoff?.blockedPeriodsConnection?.nodes?.find(
                    (lt) => lt.id === blockedPeriodIdParam
                  )
                  return {
                    title: blockedPeriod?.name,
                    ancestors: [
                      {
                        title: i18n.t('settings.time_off.blocked_periods.title'),
                        to: navigation.settings.timeoffPolicies.blockedPeriods.fullPath(),
                      },
                    ],
                  }
                }
                return {
                  ancestors: [
                    {
                      title: i18n.t('settings.time_off.blocked_periods.title'),
                      to: navigation.settings.timeoffPolicies.blockedPeriods.fullPath(),
                    },
                  ],
                }
              }),
          },
          children: [
            {
              index: true,
              element: <BlockedPeriodsDetailsPage />,
            },
          ],
        },
      ],
    },
  ]
}

export default settingsRoutes
